/**
 * PayPal (and any off-site provider) approval in a POPUP instead of a full-page
 * redirect, so the buyer never loses the checkout they were looking at.
 *
 * Nothing about the payment itself changes: the server still creates the Orders v2
 * order and the provider still redirects to its own approval page. What changes is
 * WHICH window it redirects — a child window, which reports the outcome back to the
 * checkout and closes itself.
 *
 * Both halves live here because they are one contract: the popup's message shape has
 * to match what the opener listens for, and a change to either alone silently strands
 * a buyer mid-payment.
 *
 * THE THING THAT MAKES THIS NON-OBVIOUS: `window.opener` does not survive the trip.
 * A popup that navigates to paypal.com and back has its opener severed (measured in
 * Chrome against the real sandbox), so the obvious `window.opener.postMessage` never
 * arrives — and worse, a popup that keys off `window.opener` to recognise itself
 * decides it is an ordinary page and boots the entire storefront checkout inside a
 * 500px window, where the buyer finishes paying while the real tab sits stale.
 *
 * So the popup recognises itself from `pay_popup=1`, which the SERVER put on the
 * return URL, and reports over a BroadcastChannel, which is same-origin by
 * definition and needs no window relationship at all. `window.close()` still works
 * on a severed popup, which is the one part of the original design that survived.
 */

/** Broadcast payload. Deliberately narrow — the opener acts on it. */
export type PayPopupMessage = {
  source: "dk-pay-popup"
  /** true = the provider sent them back approved; false = cancelled / backed out. */
  approved: boolean
  /** Which draft this concerns, so a stale window cannot finish a different one. */
  draft: string
}

const SOURCE = "dk-pay-popup"
const CHANNEL = "dk-pay"
/** Fallback channel for browsers without BroadcastChannel (Safari < 15.4). */
const LS_KEY = "dk:pay:result"
const WINDOW_NAME = "dk_pay"

/**
 * Runs in the POPUP, as early as possible — before Vue is even created, so the
 * window closes without booting the storefront, fetching a bootstrap or flashing
 * chrome the buyer would see for a moment and wonder about.
 *
 * Returns true when this window was a payment popup and has been handled, which
 * tells the entry point not to mount the app at all.
 */
export function handlePayPopupReturn(): boolean {
  if (typeof window === "undefined") return false
  const q = new URLSearchParams(window.location.search)
  // pay_popup is put on the URL by the server when it built the return/cancel pair,
  // so this is a fact about how the payment was started, not a guess about the
  // window. The full-page fallback (popup blocked) never asks for the flag, so an
  // ordinary tab can never mistake itself for a popup.
  if (q.get("pay_popup") !== "1") return false

  reportPayOutcome({
    source: SOURCE,
    approved: q.get("pay_return") === "1",
    draft: q.get("draft") || "",
  })
  window.close()
  // close() can be refused (a window the browser did not consider script-opened
  // cannot be script-closed). Say something rather than leaving a half-loaded
  // storefront in a 500px window.
  showClosingNotice()
  return true
}

/**
 * Tells the checkout what happened. Both channels are written, not one as a
 * fallback for the other failing loudly: this runs microseconds before the window
 * closes, and a message that loses that race leaves the buyer's checkout waiting.
 * The opener settles on whichever lands first.
 */
function reportPayOutcome(msg: PayPopupMessage) {
  try {
    // Same-origin by construction — no window relationship, and unreachable from
    // any other site. It survives the opener being severed, which postMessage
    // does not.
    new BroadcastChannel(CHANNEL).postMessage(msg)
  } catch {
    /* not supported / blocked — the storage write below is the fallback */
  }
  try {
    // A write fires a `storage` event in every OTHER same-origin window. The
    // timestamp matters: writing an identical value dispatches nothing, so two
    // cancellations in a row would go unheard.
    localStorage.setItem(LS_KEY, JSON.stringify({ ...msg, ts: Date.now() }))
  } catch {
    /* private mode — the opener's closed-poll is the last line */
  }
}

function showClosingNotice() {
  const el = document.getElementById("app")
  if (!el) return
  el.textContent = "You can close this window."
  el.setAttribute(
    "style",
    "display:flex;align-items:center;justify-content:center;min-height:100vh;font:16px/1.5 system-ui,sans-serif;color:#444",
  )
}

/**
 * Opens the payment window. MUST be called synchronously from the click handler —
 * a window opened after an `await` is a pop-up blocker's textbook case — so it opens
 * blank and is pointed at the provider once the order exists.
 *
 * Returns null when the browser refused, and the caller falls back to the full-page
 * redirect. That fallback is not a nicety: pop-up blocking is common enough that a
 * checkout which only works in a popup is a checkout that sometimes does not work.
 */
export function openPayPopup(): Window | null {
  try {
    const w = window.open("", WINDOW_NAME, popupFeatures())
    if (!w) return null
    // A blank window shows the buyer nothing while the order is being created.
    w.document.write(
      '<!doctype html><meta charset="utf-8"><title>Connecting…</title>' +
        '<body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;' +
        'font:15px/1.5 system-ui,sans-serif;color:#555">Connecting to PayPal…</body>',
    )
    w.document.close()
    return w
  } catch {
    return null
  }
}

function popupFeatures(): string {
  // Roughly PayPal's own checkout window, clamped to the screen and centred on it
  // (not on the opener, which may be a narrow window on a wide display).
  const width = Math.min(500, window.screen.availWidth || 500)
  const height = Math.min(720, window.screen.availHeight || 720)
  const left = Math.max(0, ((window.screen.availWidth || width) - width) / 2)
  const top = Math.max(0, ((window.screen.availHeight || height) - height) / 2)
  return `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)},resizable=yes,scrollbars=yes`
}

/**
 * How long to wait, after seeing the window vanish, before calling it a
 * cancellation. The popup reports and then immediately closes itself, so without
 * this grace the closed-poll would routinely beat the message in and tell a buyer
 * who just paid that nothing happened.
 */
const CLOSED_GRACE_MS = 600

/**
 * Runs in the OPENER: waits for the popup to report, and treats the window simply
 * closing as a cancellation.
 *
 * The closed-poll is not redundant with the message. A buyer who closes the popup
 * with the X, or whose approval fails on PayPal's side, sends no message at all —
 * and without the poll the checkout would sit disabled behind a "processing" state
 * forever. Whichever settles first wins; the other is ignored.
 */
export function watchPayPopup(
  win: Window,
  draft: string,
  on: { approved: () => void; cancelled: () => void },
): () => void {
  let settled = false

  const finish = (approved: boolean) => {
    if (settled) return
    settled = true
    cleanup()
    if (approved) on.approved()
    else on.cancelled()
  }

  const accept = (data: unknown) => {
    const d = data as PayPopupMessage | null
    if (!d || d.source !== SOURCE) return
    // A window left open from an earlier attempt must not finish this draft.
    if (d.draft && draft && d.draft !== draft) return
    finish(!!d.approved)
  }

  let channel: BroadcastChannel | null = null
  try {
    channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = (e: MessageEvent) => accept(e.data)
  } catch {
    /* fall through to the storage listener */
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key !== LS_KEY || !e.newValue) return
    try {
      accept(JSON.parse(e.newValue))
    } catch {
      /* not ours */
    }
  }
  window.addEventListener("storage", onStorage)

  // Give an in-flight report time to land before declaring a cancellation.
  let graceAt = 0
  const timer = window.setInterval(() => {
    if (!win.closed) return
    if (!graceAt) {
      graceAt = Date.now()
      return
    }
    if (Date.now() - graceAt >= CLOSED_GRACE_MS) finish(false)
  }, 200)

  function cleanup() {
    window.clearInterval(timer)
    window.removeEventListener("storage", onStorage)
    try {
      channel?.close()
    } catch {
      /* ignore */
    }
  }

  return () => {
    settled = true
    cleanup()
  }
}
